/**
 * AdminPage - Container component for admin dashboard
 *
 * Manages admin data and delegates UI to AdminPageView.
 * Handles system health monitoring and admin operations.
 */
import { useReducer, useEffect } from 'react';
import AdminPageView from './AdminPageView';
import { fetchSystemHealth, fetchAdminMetrics } from './admin.api';
import type { SystemHealth, AdminMetrics } from './admin.schema';

interface AdminState {
  systemHealth: SystemHealth | null;
  metrics: AdminMetrics | null;
  loading: boolean;
  error: string | null;
}

type AdminAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ADMIN_DATA'; payload: { systemHealth: SystemHealth; metrics: AdminMetrics } }
  | { type: 'RESET_STATE' };

const adminReducer=(state:AdminState,action:AdminAction):AdminState=>{
    switch(action.type){
        case'SET_LOADING':return{...state,loading:action.payload};
        case'SET_ERROR':return{...state,error:action.payload,loading:false};
        case'SET_ADMIN_DATA':return{...state,systemHealth:action.payload.systemHealth,metrics:action.payload.metrics,error:null,loading:false};
        case'RESET_STATE':return{systemHealth:null,metrics:null,loading:true,error:null};
        default:return state;
    }
};

const AdminPage=()=>{
    console.log('AdminPage is running');
    const[state,dispatch]=useReducer(adminReducer,{systemHealth:null,metrics:null,loading:true,error:null});
    const envKey=import.meta.env.VITE_QreditCharge_API_KEY as string|undefined;
    const lsKey=typeof window!=='undefined'?window.localStorage?.getItem('QreditCharge_API_KEY')||undefined:undefined;
    const apiKeyMissing=!envKey&&!lsKey;
    useEffect(()=>{
        const loadAdminData=async()=>{
            try{
                const[healthData,metricsData]=await Promise.all([fetchSystemHealth(),fetchAdminMetrics()]);
                dispatch({type:'SET_ADMIN_DATA',payload:{systemHealth:healthData,metrics:metricsData}});
            }catch(err){
                dispatch({type:'SET_ERROR',payload:err instanceof Error?err.message:'Failed to load admin data'});
            }finally{
                dispatch({type:'SET_LOADING',payload:false});
            }
        };
        loadAdminData();
    },[]);
    console.log('AdminPage is returning admin page');
    return(<AdminPageView systemHealth={state.systemHealth}metrics={state.metrics}loading={state.loading}error={state.error}apiKeyMissing={apiKeyMissing}/>);
};

export default AdminPage;
